import { Box, Button, HStack, VStack } from "@chakra-ui/react"
import { FormikWithDiscardFormState } from "@pathwright/ux/src/components/form/state/FormikDiscardFormState"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { FormikProps } from "formik"
import { useCallback, useEffect, useMemo, useState } from "react"
import RichTextEditorWithMediaManager from "../../media-manager/RichTextEditorWithMediaManager"
import { useCoverPageContext } from "../CoverPageProvider"
import { CoverPagePanelSectionKey } from "../types"
import { COVER_PAGE_INLINE_EDITOR_SOFT_STYLES_SPACIOUS } from "../ui"
import { useCoverPageTranslate } from "../utils"
import { CoverPageSectionEditTriggerButton } from "./CoverPageSectionTrigger"

const COVER_PAGE_INLINE_SECTION_EDIT_REQUEST_EVENT =
  "cover-page:inline-section-edit-request"

type CoverPageInlineSectionEditRequestDetail = {
  sectionKey: CoverPagePanelSectionKey
}

export function requestCoverPageInlineSectionEdit(
  sectionKey: CoverPagePanelSectionKey
) {
  if (typeof window === "undefined") return

  window.dispatchEvent(
    new CustomEvent<CoverPageInlineSectionEditRequestDetail>(
      COVER_PAGE_INLINE_SECTION_EDIT_REQUEST_EVENT,
      {
        detail: { sectionKey }
      }
    )
  )
}

export type UseCoverPageInlineSectionEditorArgs = {
  sectionKey: CoverPagePanelSectionKey
}

export type CoverPageInlineSectionEditorState = ReturnType<
  typeof useCoverPageInlineSectionEditor
>

export function useCoverPageInlineSectionEditor({
  sectionKey
}: UseCoverPageInlineSectionEditorArgs) {
  const resourceId = useCoverPageContext((ctx) => ctx.resourceId)
  const [isSectionEditing, setIsSectionEditing] = useState(false)

  const startEditing = useCallback(() => {
    setIsSectionEditing(true)
  }, [])

  const finishEditing = useCallback(() => {
    setIsSectionEditing(false)
  }, [])

  // Navigating to another cover page exits local edit mode. Draft persistence is
  // keyed by resource, so unmounting or changing resources does not lose edits.
  useEffect(() => {
    setIsSectionEditing(false)
  }, [resourceId])

  useEffect(() => {
    if (typeof window === "undefined") return

    const handleEditRequest = (event: Event) => {
      const detail = (
        event as CustomEvent<CoverPageInlineSectionEditRequestDetail>
      ).detail

      if (detail?.sectionKey === sectionKey) {
        setIsSectionEditing(true)
      }
    }

    window.addEventListener(
      COVER_PAGE_INLINE_SECTION_EDIT_REQUEST_EVENT,
      handleEditRequest
    )

    return () => {
      window.removeEventListener(
        COVER_PAGE_INLINE_SECTION_EDIT_REQUEST_EVENT,
        handleEditRequest
      )
    }
  }, [sectionKey])

  return {
    sectionKey,
    isSectionEditing,
    startEditing,
    finishEditing
  }
}

type CoverPageInlineSectionEditorValues = {
  value: string
}

type CoverPageInlineSectionEditorProps = {
  sectionKey: CoverPagePanelSectionKey
  value: string
  triggerLabel: string
  onSave: (draft: string) => Promise<void>
}

function CoverPageInlineSectionEditor({
  sectionKey,
  value,
  triggerLabel,
  onSave
}: CoverPageInlineSectionEditorProps) {
  const { t } = useCoverPageTranslate()
  const resourceId = useCoverPageContext((v) => v.resourceId)
  const editor = useCoverPageInlineSectionEditor({
    sectionKey
  })
  const initialValues = useMemo<CoverPageInlineSectionEditorValues>(
    () => ({ value }),
    [value]
  )
  const mediaManagerContextKey = useMemo(
    () => `resource-${resourceId}`,
    [resourceId]
  )

  return (
    <FormikWithDiscardFormState
      key={`${sectionKey}:${resourceId}`}
      formStateKey={["cover-page", "inline-section", sectionKey, resourceId]}
      initialValues={initialValues}
      enableReinitialize
      formStateOnSubmit="preserve"
      onSubmit={async (values: CoverPageInlineSectionEditorValues) => {
        if (values.value !== value) {
          await onSave(values.value)
        }

        editor.finishEditing()
      }}
    >
      {(form: FormikProps<CoverPageInlineSectionEditorValues>) => (
        <Box position="relative" w="full">
          {!editor.isSectionEditing ? (
            <HStack justify="flex-end" w="full" p={0}>
              <CoverPageSectionEditTriggerButton
                label={triggerLabel}
                ariaLabel={
                  form.dirty
                    ? t("inline_section_editor.unsaved_trigger_label", {
                        defaultValue: "{{label}} (unsaved changes)",
                        label: triggerLabel
                      })
                    : triggerLabel
                }
                onClick={editor.startEditing}
                isDirty={form.dirty}
              />
            </HStack>
          ) : null}

          <VStack align="stretch" gap={3}>
            {editor.isSectionEditing ? (
              <HStack justify="flex-end" px={2} gap={4}>
                <Button
                  size="sm"
                  // @ts-expect-error - TODO: get typing correct using chakra typegen https://chakra-ui.com/docs/get-started/cli#chakra-typegen
                  variant="link"
                  color="gray.400"
                  onClick={() => {
                    editor.finishEditing()
                    form.resetForm()
                    void form.validateForm(form.initialValues)
                  }}
                  disabled={form.isSubmitting}
                >
                  <Pathicon icon="x" size="0.8em" />
                  {t("panel.discard", { defaultValue: "Discard" })}
                </Button>
                <Button
                  size="sm"
                  colorPalette="brand"
                  onClick={() => void form.submitForm()}
                  disabled={form.isSubmitting || !form.dirty}
                >
                  {form.isSubmitting
                    ? t("panel.saving", { defaultValue: "Saving..." })
                    : t("panel.save_changes", {
                        defaultValue: "Save Changes"
                      })}
                </Button>
              </HStack>
            ) : null}
            <Box>
              <RichTextEditorWithMediaManager
                value={
                  editor.isSectionEditing ? form.values.value ?? "" : value
                }
                onChange={(value) => {
                  if (!editor.isSectionEditing) return
                  void form.setFieldValue("value", value, false)
                }}
                mediaManagerContextKey={mediaManagerContextKey}
                mode={editor.isSectionEditing ? "edit" : "view"}
                controlBar={{
                  showEditButton: false,
                  showSaveCancelButtons: false
                }}
                editorStyles={COVER_PAGE_INLINE_EDITOR_SOFT_STYLES_SPACIOUS}
                autoFocus
              />
            </Box>
          </VStack>
        </Box>
      )}
    </FormikWithDiscardFormState>
  )
}

export default CoverPageInlineSectionEditor
